import Translation from "src/translations/locales/translation";

import { auth } from "src/firebase";
import { RoutesEnum } from "src/enums/Routes";
import { useTranslation } from "react-i18next";
import { signInWithCustomToken } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FirebaseAuthErrorHandler } from "src/utils/FirebaseAuthErrorHandler";
import { SimpleElevatedButton } from "src/shared-components/form/fields/buttons/simple-elevated-button";

export function AuthResponse() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Read once: the query is dropped from the address right after, so the
  // sign-in token doesn't stay in the browser's history or reach error
  // reports.
  const [callbackResponse] = useState<any>(() =>
    Object.fromEntries(new URLSearchParams(location.search))
  );

  useEffect(() => {
    if (location.search) navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Memoize the handleRedirect function to prevent ESLint warning
  const handleRedirect = useCallback(async () => {
    setIsLoading(true);

    const { error, token, redirectURI } = callbackResponse;

    if (token) {
      try {
        await signInWithCustomToken(auth, token);
        window.location.href = redirectURI
          ? RoutesEnum.shop
          : RoutesEnum.profile;
      } catch (e) {
        const error = FirebaseAuthErrorHandler.getMessage(e, t);
        setErrorMessage(error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Without a token or an error, e.g. when the page is loaded again after
      // its query was dropped: says so, with the way back to sign in.
      setErrorMessage(error || t(Translation.common.errors.firebase.unknown));
      setIsLoading(false);
    }
  }, [t, callbackResponse]); // Include 't' as a dependency to prevenet ESLint warning

  useEffect(() => {
    handleRedirect();
  }, [handleRedirect]);

  return (
    <div className="w-full h-[100svh] flex justify-center items-center px-6">
      {/* loader */}
      {isLoading && <span className="loading loading-spinner loading-md" />}

      {/* show error message and redirect button */}
      {errorMessage && (
        <div className="flex flex-col gap-6 justify-center items-center w-full max-w-[500px]">
          <p>{errorMessage}</p>
          <SimpleElevatedButton
            id="authResponse-redirect"
            text={t(Translation.general.backToSignIn)}
            onClick={() => window.location.replace(RoutesEnum.signin)}
          />
        </div>
      )}
    </div>
  );
}
